import React, { useContext, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import { connect } from 'react-redux';
import config from '@config/config';

import { ILogout } from '@src/actions/auth';
import { SentinelUser } from '@modules/auth/components/types/user';
import { CustomerAccountInfo } from '@modules/auth/components/types/customer-account';
import AccountInfoWidget from '@src/modules/Dashboard/AccountInfo';
import OAuthClient from '@modules/auth/widgets/OAuthClient';
import { styled } from '@modules/common/components/ThemeProvider/utils';
import UserFormHeader from '@src/components/common/UserFormHeader';
import UserForm from '@modules/user-settings/components/UserForm';
import { StoreState } from '@src/meta/types/store';
import BasicInfo from '@modules/user-settings/components/Account/BasicInfo';
import { UserDataComplianceError } from '@modules/billing/components/Checkout/Compliance/ComplianceError';
import isUserNotCompliant from '@modules/auth/components/lib/compliance';
import DashboardCard from '@modules/common/components/DashboardCard';

import PromoCode from './PromoCode';
import { AuthenticationContext } from '../../../Authentication/components/context/index';

type Props = {
  configurableUser: SentinelUser;
  logout: () => Promise<ILogout>;
  passwordChangeUrlClick: () => void;
  updateUser: (user: SentinelUser) => void;
};

type PropsFromStore = {
  shAccount: CustomerAccountInfo | undefined;
  userComplianceFlagged: boolean;
  projectId: string;
};

const Account = ({
  configurableUser,
  passwordChangeUrlClick,
  updateUser,
  logout,
  shAccount,
  userComplianceFlagged,
  projectId,
}: Props & PropsFromStore) => {
  const context = useContext(AuthenticationContext);
  const [openNewsletterModal, setOpenNewsletterModal] = useState<boolean>(false);
  return (
    <StyledAccountGrid className="account-settings" stackable>
      <Grid.Row columns={2}>
        <Grid.Column className="user-info">
          <DashboardCard noPadding>
            <UserFormHeader userLabel="User Info">
              <BasicInfo user={configurableUser} />
            </UserFormHeader>

            {userComplianceFlagged && (
              <StyledMsgWrapper>
                <UserDataComplianceError />
              </StyledMsgWrapper>
            )}

            <UserForm
              setOpenNewsletterModal={setOpenNewsletterModal}
              openNewsletterModal={openNewsletterModal}
              configurableUser={configurableUser}
              logout={logout}
              updateUser={updateUser}
              changePasswordClick={passwordChangeUrlClick}
              accountId={projectId}
              disabled={userComplianceFlagged}
            />

            <PromoCode />
          </DashboardCard>
        </Grid.Column>

        <Grid.Column className="account-info">
          <AccountInfoWidget />
          {shAccount && <OAuthClient />}
        </Grid.Column>
      </Grid.Row>
    </StyledAccountGrid>
  );
};

const StyledAccountGrid = styled(Grid)`
  padding: ${props => props.theme.mainContentPadding} !important;

  .account-info {
    height: fit-content;
  }
`;

const StyledMsgWrapper = styled('div')`
  padding: 0 2rem;
  background: ${config.backgroundGrey};
  margin-top: -1rem;
`;

const mapStateToProps = (state: StoreState): PropsFromStore => ({
  shAccount: state.account.account,
  userComplianceFlagged: isUserNotCompliant(state.auth.configurableUser),
  projectId: state.project.project.id,
});

export default connect(mapStateToProps)(Account);
